import CommonUtils from 'utils/CommonUtils';
import Location from 'domain/Location';

const get = (eventId) => {
    return {
        type: 'GET_EVENT',
        payload: (async () => {
            const query = new Parse.Query('Event');
            query.include(['type']);
            const event = await query.get(eventId);
            
            const locationQuery = new Parse.Query('EventLocation');
            locationQuery.equalTo('event', event);
            locationQuery.include(['location', 'location.parent']);
            const eventLocations = await locationQuery.find();
            
            event.set('locations', eventLocations.map(el => new Location(el.get('location'))));
            return event;
        })()
    };
};

const list = ({ page = 1, pageSize = 20, loadMore = false, ...filters } = {}) => {
    return {
        type: loadMore ? 'LOAD_MORE_EVENT' : 'LIST_EVENT',
        payload: (async () => {
            const query = new Parse.Query('Event');
            query.include(['type']);
            query.descending('createdAt');
            
            if (filters.type) {
                query.equalTo('type', filters.type);
            }
            if (filters.startDate) {
                query.greaterThanOrEqualTo('startDate', new Date(filters.startDate));
            }
            if (filters.endDate) {
                query.lessThanOrEqualTo('endDate', new Date(filters.endDate));
            }
            if (filters.name) {
                query.contains('name', filters.name);
            }
            
            const skip = (page - 1) * pageSize;
            query.skip(skip);
            query.limit(pageSize + 1);
            
            const results = await query.find();
            const hasMore = results.length > pageSize;
            const data = hasMore ? results.slice(0, pageSize) : results;
            
            return {
                data,
                pagination: {
                    page,
                    pageSize,
                    hasMore,
                    total: data.length
                }
            };
        })()
    };
};

const save = (eventData) => {
    return {
        type: 'SAVE_EVENT',
        payload: (async () => {
            let event;
            
            if (eventData.objectId) {
                const query = new Parse.Query('Event');
                event = await query.get(eventData.objectId);
            } else {
                event = new Parse.Object('Event');
            }
            
            event.set('name', eventData.name);
            event.set('description', eventData.description);
            event.set('note', eventData.note);
            event.set('startDate', new Date(eventData.startDate));
            event.set('endDate', new Date(eventData.endDate));
            event.set('type', CommonUtils.createPointer('EventType', eventData.type));
            event.set('dateSpecificity', eventData.dateSpecificity);

            const savedEvent = await event.save();
            
            if (eventData.locations && Array.isArray(eventData.locations)) {
                const existingLocationQuery = new Parse.Query('EventLocation');
                existingLocationQuery.equalTo('event', savedEvent);
                const existingLocations = await existingLocationQuery.find();
                
                if (existingLocations.length > 0) {
                    await Parse.Object.destroyAll(existingLocations);
                }
                
                const newEventLocations = eventData.locations.map(locationId => {
                    const eventLocation = new Parse.Object('EventLocation');
                    eventLocation.set('event', savedEvent);
                    eventLocation.set('location', CommonUtils.createPointer('Location', locationId));
                    return eventLocation;
                });
                
                if (newEventLocations.length > 0) {
                    await Parse.Object.saveAll(newEventLocations);
                }
            }

            return savedEvent;
        })()
    };
};

const update = (eventId, eventData) => {
    return {
        type: 'UPDATE_EVENT',
        payload: (async () => {
            const event = new Parse.Object('Event');
            event.id = eventId;
            
            if (eventData.name !== undefined) event.set('name', eventData.name);
            if (eventData.description !== undefined) event.set('description', eventData.description);
            if (eventData.note !== undefined) event.set('note', eventData.note);
            if (eventData.startDate !== undefined) event.set('startDate', new Date(eventData.startDate));
            if (eventData.endDate !== undefined) event.set('endDate', new Date(eventData.endDate));
            if (eventData.type !== undefined) event.set('type', CommonUtils.createPointer('EventType', eventData.type));
            if (eventData.dateSpecificity !== undefined) event.set('dateSpecificity', eventData.dateSpecificity);

            return await event.save();
        })()
    };
};

const remove = (eventId) => {
    return {
        type: 'DELETE_EVENT',
        payload: (async () => {
            const eventPointer = CommonUtils.createPointer('Event', eventId);
            
            const observationQuery = new Parse.Query('Observation');
            observationQuery.equalTo('event', eventPointer);
            const observationCount = await observationQuery.count();
            
            if (observationCount > 0) {
                throw new Error(`Cannot delete event: ${observationCount} observations reference this event`);
            }
            
            const voucherQuery = new Parse.Query('EvidenceVoucher');
            voucherQuery.equalTo('event', eventPointer);
            const voucherCount = await voucherQuery.count();
            
            const literatureQuery = new Parse.Query('EvidenceLiterature');
            literatureQuery.equalTo('event', eventPointer);
            const literatureCount = await literatureQuery.count();
            
            const fileQuery = new Parse.Query('EvidenceFile');
            fileQuery.equalTo('event', eventPointer);
            const fileCount = await fileQuery.count();
            
            if (voucherCount > 0 || literatureCount > 0 || fileCount > 0) {
                throw new Error(`Cannot delete event: ${voucherCount + literatureCount + fileCount} evidence records reference this event`);
            }
            
            const noteQuery = new Parse.Query('Note');
            noteQuery.equalTo('event', eventPointer);
            const notes = await noteQuery.find();
            
            if (notes.length > 0) {
                await Parse.Object.destroyAll(notes);
            }
            
            const event = new Parse.Object('Event');
            event.id = eventId;
            await event.destroy();
            
            return { 
                id: eventId,
                deletedNotesCount: notes.length
            };
        })()
    };
};

const clear = () => {
    return {
        type: 'CLEAR_EVENT'
    };
};

const setCurrent = (event) => {
    return {
        type: 'SET_CURRENT_EVENT',
        payload: event
    };
};

export default {
    get,
    list,
    save,
    update,
    remove,
    clear,
    setCurrent
};